/** Single classified fetch attempt for the standardized `tools/http/fetch` transport: timeout-raced global `fetch`
 * with retry/no-retry classification of the response or thrown error. The abort/timeout/retryability helpers are
 * injected by the tools composition root (utils leaves may not import each other).
 *
 * THE BAND PREDICATES ARE NOT INJECTED. `isServerError` and `isRedirect` are value-imported straight from
 * `@federa/const/http-status`, which a `utils/` leaf may reach freely, so they need no `deps` member at all: a seam
 * carrying them would exist for the layer floor rather than for any caller, and nothing varies it. What stays on
 * `FetchAttemptDeps` is the genuinely host-bound set -- the abort/timeout pair and the error classifier -- so every
 * remaining member is one a caller could actually substitute.
 *
 * The raced-fetch block below is spelled again in `fetch-single-attempt.ts`; folding the two was ADJUDICATED AND
 * DECLINED, and the reasoning sits once at the `runWithRetry` binding in `tools/http/fetch.ts`, which composes both.
 * What is local to THIS arm is the reason its half cannot rethrow: it owes the retry loop a classified
 * `{ response | error, shouldRetry }` envelope, so the throw is consumed here and never leaves. */

import { HTTP_STATUS, isRedirect, isServerError } from '@federa/const/http-status';
import type { FetchAttemptFn, HttpAbortTimeoutSeams } from '@federa/types/http';
import type { HttpRetryConfig } from '@federa/types/http-api';

/** Host seams for the attempt executor: the shared abort/timeout pair plus the error classifier only this arm takes. */
export interface FetchAttemptDeps extends HttpAbortTimeoutSeams {
	isRetryableError: (error: unknown, config: HttpRetryConfig) => boolean;
}

function isRetryableStatus(status: number, config: HttpRetryConfig): boolean {
	if (config.retryOn5xx && isServerError(status)) return true;
	return config.retryOn429 && status === HTTP_STATUS.TOO_MANY_REQUESTS;
}

/** Bind the per-attempt executor: success (ok/redirect) and non-retryable responses return immediately; retryable
 * responses/errors carry `shouldRetry` while attempts remain; an external-signal abort never retries. */
export function makeFetchAttempt(deps: FetchAttemptDeps): FetchAttemptFn {
	return async ({ url, options, config, attempt }) => {
		const { signalledInit, originalSignal, timeoutPromise, cleanup } = deps.setupAbortController(options, config.timeout);
		// The ONE retry decision, read the same way for a retryable RESPONSE and a retryable ERROR: attempts remain.
		const retriesRemain = attempt < config.maxRetries;
		let fetchPromise: Promise<Response> | undefined;
		try {
			fetchPromise = fetch(url, signalledInit);
			const response = await Promise.race([fetchPromise, timeoutPromise]);
			if (response.ok || isRedirect(response.status)) return { response, shouldRetry: false };
			if (!isRetryableStatus(response.status, config)) return { response, shouldRetry: false };
			return { response, shouldRetry: retriesRemain };
		} catch (error) {
			deps.handleTimeoutError(error, fetchPromise);
			if (originalSignal?.aborted) return { error, shouldRetry: false };
			if (!deps.isRetryableError(error, config)) return { error, shouldRetry: false };
			return { error, shouldRetry: retriesRemain };
		} finally {
			cleanup();
		}
	};
}
